import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, CircleAlert, Loader, Search } from 'lucide-react';
import { format } from 'date-fns';
import { useDataSource } from '../context/DataSourceContext';
import { useTitle } from '../context/TitleContext';
import { getCustomers } from '../services/dataService';
import { Customer } from '../data/mockData';
import ReminderButton from './ReminderButton';
import { ITEMS_PER_PAGE } from '../data/const_vars';

type SortField = 'caseId' | 'caseStatus' | 'name' | 'phoneNumber' | 'documents' | 'lastUpload';
type SortDirection = 'asc' | 'desc';

const DASHBOARD_STATE_KEY = 'dashboardState';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { setTitle } = useTitle();
  const { useAwsDynamoDB, awsConfig } = useDataSource();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial values from URL params or localStorage
  const getInitialValue = <T,>(key: string, defaultValue: T): T => {
    // Try to get from URL first
    const urlValue = searchParams.get(key);
    if (urlValue) return urlValue as unknown as T;
    
    // Then try localStorage
    try {
      const savedState = localStorage.getItem(DASHBOARD_STATE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        if (parsedState[key] !== undefined) return parsedState[key];
      }
    } catch (e) {
      console.error('Error parsing saved dashboard state', e);
    }
    
    // Fall back to default
    return defaultValue;
  };

  const [searchTerm, setSearchTerm] = useState<string>(getInitialValue('searchTerm', ''));
  const [sortField, setSortField] = useState<SortField>(getInitialValue('sortField', 'caseId'));
  const [sortDirection, setSortDirection] = useState<SortDirection>(getInitialValue('sortDirection', 'asc'));
  const [currentPage, setCurrentPage] = useState<number>(Number(getInitialValue('currentPage', 1)));
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [paginatedCustomers, setPaginatedCustomers] = useState<Customer[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  // Set title on dashboard
  useEffect(() => {
    setTitle(t('adminPortal'));
    document.title = t('adminPortal');
  }, [setTitle, t]);

  // Load customers from selected data source
  useEffect(() => {
    const loadCustomers = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCustomers(useAwsDynamoDB, awsConfig);
        setCustomers(data);
      } catch (err) {
        console.error('Error loading customers:', err);
        setError(t('errorLoadingData'));
      } finally {
        setLoading(false);
      }
    };

    loadCustomers();
  }, [useAwsDynamoDB, awsConfig, t]);

  // Filter and sort customers
  useEffect(() => {
    if (customers.length === 0) {
      setFilteredCustomers([]);
      return;
    }

    const sorted = [...customers].sort((a, b) => {
      if (sortField === 'caseId') {
        return sortDirection === 'asc'
          ? a.caseId.localeCompare(b.caseId)
          : b.caseId.localeCompare(a.caseId);
      } else if (sortField === 'name') {
        return sortDirection === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (sortField === 'phoneNumber') {
        return sortDirection === 'asc'
          ? a.phoneNumber.localeCompare(b.phoneNumber)
          : b.phoneNumber.localeCompare(a.phoneNumber);
      } else if (sortField === 'documents') {
        // Count completed required documents for each customer
        const aCompletedRequired = a.requiredDocuments.filter(req => 
          a.uploadedDocuments.some(doc => doc.requirementId === req.id)
        ).length;
        const bCompletedRequired = b.requiredDocuments.filter(req => 
          b.uploadedDocuments.some(doc => doc.requirementId === req.id)
        ).length;
        
        const aRatio = aCompletedRequired / a.requiredDocuments.length;
        const bRatio = bCompletedRequired / b.requiredDocuments.length;
        
        return sortDirection === 'asc' ? aRatio - bRatio : bRatio - aRatio;
      } else if (sortField === 'lastUpload') {
        const aDate = a.lastUploadDate ? new Date(a.lastUploadDate).getTime() : 0;
        const bDate = b.lastUploadDate ? new Date(b.lastUploadDate).getTime() : 0;
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      return 0;
    });

    const filtered = sorted.filter(customer =>
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.caseId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phoneNumber.includes(searchTerm)
    );

    setFilteredCustomers(filtered);
  }, [customers, searchTerm, sortField, sortDirection]);

  // Save dashboard state to localStorage and URL whenever it changes
  useEffect(() => {
    const state = {
      searchTerm,
      sortField,
      sortDirection,
      currentPage
    };
    
    localStorage.setItem(DASHBOARD_STATE_KEY, JSON.stringify(state));
    
    // Update URL params
    setSearchParams({
      searchTerm,
      sortField,
      sortDirection,
      currentPage: currentPage.toString()
    });
  }, [searchTerm, sortField, sortDirection, currentPage, setSearchParams]);

  // Paginate the filtered data
  useEffect(() => {
    const paginateData = async () => {
      if (filteredCustomers.length === 0) {
        setPaginatedCustomers([]);
        setTotalPages(1);
        return;
      }

      setPageLoading(true);

      // Simulate network delay for page loading
      await new Promise(resolve => setTimeout(resolve, 300));

      const totalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE);
      setTotalPages(totalPages);

      // Ensure current page is valid
      const validPage = Math.max(1, Math.min(currentPage, totalPages));
      if (validPage !== currentPage) {
        setCurrentPage(validPage);
        return;
      }

      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE;
      const paginatedItems = filteredCustomers.slice(start, end);
      
      setPaginatedCustomers(paginatedItems);
      setPageLoading(false);
    };

    paginateData();
  }, [filteredCustomers, currentPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />;
  };

  const getDocumentStatusColor = (customer: Customer) => {
    const completedRequired = customer.requiredDocuments.filter(req => 
      customer.uploadedDocuments.some(doc => doc.requirementId === req.id)
    ).length;
    
    if (completedRequired === 0) return 'text-red-500';
    if (completedRequired < customer.requiredDocuments.length) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getCaseStatusColor = (customer: Customer) => {
    const currentCaseStatus = customer.caseStatus;
    
    if (currentCaseStatus === 'In Progress') return 'font-semibold text-yellow-600 dark:text-yellow-200';
    if (currentCaseStatus === 'Complete') return 'font-semibold text-green-500';

  }; 

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'MMM d, yyyy HH:mm');
  };

  // Update a single customer in the list (used for reminder updates)
  const updateCustomer = useCallback((updatedCustomer: Customer) => {
    // Update in all relevant states to avoid refresh flash
    setCustomers(prev => 
      prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c)
    );
    setFilteredCustomers(prev =>
      prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c)
    );
    setPaginatedCustomers(prev =>
      prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c)
    );
  }, []);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 mt-12">
        <Loader className="h-8 w-8 text-blue-500 animate-spin" />
        <p className="text-gray-600 dark:text-gray-400">{t('loadingData')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 mt-12">
        <CircleAlert className="h-8 w-8 text-red-500" />
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      <div className="max-w-xl mx-auto w-full">
      <div className="flex items-center space-x-4">
        <h1 className="text-lg font-bold pr-[44]">Current Requests</h1>
        <div className="relative flex-1">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder={t('search')}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        </div>
      </div>
      </div>

      {useAwsDynamoDB && (
        <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-3 flex items-center">
          <div className="w-full text-center text-sm text-blue-700 dark:text-blue-300">
            {t('usingDynamoDB')} ({awsConfig.tableName})
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('caseId')}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>{t('caseId')}</span>
                  {renderSortIcon('caseId')}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('caseStatus')}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>{t('caseStatus')}</span>
                  {renderSortIcon('caseStatus')}
                </div>
              </th>              
              <th
                scope="col"
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>{t('customerName')}</span>
                  {renderSortIcon('name')}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('phoneNumber')}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>{t('phoneNumber')}</span>
                  {renderSortIcon('phoneNumber')}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('documents')}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>{t('documents')}</span>
                  {renderSortIcon('documents')}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('lastUpload')}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>{t('lastUpload')}</span>
                  {renderSortIcon('lastUpload')}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {t('actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
            {pageLoading ? (
              <tr>
                <td colSpan={6} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <Loader className="h-6 w-6 text-blue-500 animate-spin mb-2" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Loading data...</p>
                  </div>
                </td>
              </tr>
            ) : paginatedCustomers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No results found
                </td>
              </tr>
            ) : (
              paginatedCustomers.map((customer, index) => {
                const completedRequired = customer.requiredDocuments.filter(req => 
                  customer.uploadedDocuments.some(doc => doc.requirementId === req.id)
                ).length;
                
                return (
                  <tr 
                    key={customer.id} 
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      index % 2 === 0 
                        ? 'bg-white dark:bg-gray-900' 
                        : 'bg-gray-50 dark:bg-gray-800/50'
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                      <Link
                        to={`/case/${customer.caseId}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {customer.caseId}
                      </Link>
                    </td>
                    <td className="caseStatusRowDisplay px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                      <span className={getCaseStatusColor(customer)}>{customer.caseStatus}</span>
                      
                    </td>                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 text-center">
                      {customer.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 text-center">
                      {customer.phoneNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <div className="flex flex-col items-center">
                        <span
                          className={`font-medium ${getDocumentStatusColor(customer)}`}
                        >
                          {completedRequired}/{customer.requiredDocuments.length}
                        </span>
                        
                        {completedRequired < customer.requiredDocuments.length && (
                          <ReminderButton 
                            customer={customer}
                            onReminderSent={updateCustomer}
                            compact
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 text-center">
                      {formatDate(customer.lastUploadDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <Link
                        to={`/case/${customer.caseId}`}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-500"
                      >
                        {t('view')}
                      </Link>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {filteredCustomers.length > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1 || pageLoading}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">First</span>
              <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || pageLoading}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || pageLoading}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages || pageLoading}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">Last</span>
              <ChevronsRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Showing <span className="font-medium">{paginatedCustomers.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * ITEMS_PER_PAGE, filteredCustomers.length)}
                </span>{' '}
                of <span className="font-medium">{filteredCustomers.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                {/* First Page Button */}
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || pageLoading}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">First</span>
                  <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {/* Previous Page Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || pageLoading}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  
                  // Display pages around the current page
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1; // Show first 5 pages
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i; // Show last 5 pages
                  } else {
                    pageNum = currentPage - 2 + i; // Show 2 pages before and after current
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={pageLoading}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? 'z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 dark:border-blue-500 text-blue-600 dark:text-blue-300'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      } ${pageLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                {/* Next Page Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || pageLoading}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {/* Last Page Button */}
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || pageLoading}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Last</span>
                  <ChevronsRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
